/** One native turn's bounded capacity recovery, owned and counted by the server. */
export interface CapacityRetryState {
  ownerThreadId: string;
  threadId: string;
  turnId: string;
  cycleId: string;
  /** Monotonically increasing within the owning Web conversation. */
  revision: number;
  status: 'waiting' | 'retrying' | 'exhausted' | 'resolved' | 'stopped';
  /** The automatic retry currently scheduled or attempted; excludes the initial request. */
  retryCount: number;
  maxRetries: 5;
  nextRetryAt?: string;
}

export interface CapacityRetryRequest {
  cycleId: string;
  revision: number;
}

export interface CapacityRetryResponse {
  capacityRetryState: CapacityRetryState;
}

export interface CapacityRetryEvent {
  method: 'reados/capacityRetry';
  params: CapacityRetryState & Record<string, unknown>;
}

export function isCapacityRetryState(value: unknown): value is CapacityRetryState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const state = value as Record<string, unknown>;
  const identifier = (item: unknown) => typeof item === 'string' && item.length > 0 && item.length <= 200;
  return ['ownerThreadId', 'threadId', 'turnId', 'cycleId'].every(key => identifier(state[key]))
    && Number.isSafeInteger(state.revision) && Number(state.revision) > 0
    && ['waiting', 'retrying', 'exhausted', 'resolved', 'stopped'].includes(String(state.status))
    && Number.isSafeInteger(state.retryCount) && Number(state.retryCount) >= 0 && Number(state.retryCount) <= 5
    && state.maxRetries === 5
    && (state.nextRetryAt === undefined || typeof state.nextRetryAt === 'string'
      && state.nextRetryAt.length <= 40 && Number.isFinite(Date.parse(state.nextRetryAt)));
}

export function isCapacityRetryEvent(value: unknown): value is CapacityRetryEvent {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && (value as Record<string, unknown>).method === 'reados/capacityRetry'
    && isCapacityRetryState((value as Record<string, unknown>).params);
}
